import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Paper, Button, TextField,
    IconButton, CircularProgress, Alert, Autocomplete, Chip, Avatar, FormHelperText
} from '@mui/material';
import {
    Add as AddIcon, PhotoCamera as PhotoCameraIcon, Close as CloseIcon,
    CloudUpload as UploadIcon, LocalOffer as TagIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import contentService from '../services/contentService';
import productService from '../services/productService';
import '../styles/ProtectedRoutes.css';

const CreatePostPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ title: '', caption: '' });
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});   // ← now an object, not a string
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    useEffect(() => { loadProducts(); }, []);

    const loadProducts = async () => {
        try {
            setLoadingProducts(true);
            const response = await productService.getCustomerProducts({ category: 'Ready Made' }, 200, 0);
            if (response.success) setProducts(response.data);
        } catch (err) {
            console.error('Failed to load products:', err);
        } finally {
            setLoadingProducts(false);
        }
    };

    const clearFieldError = (field) =>
        setErrors(prev => ({ ...prev, [field]: '' }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        clearFieldError(name);
    };

    const handleMediaChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg','image/jpg','image/png','image/gif','image/webp','video/mp4','video/webm'];
        if (!validTypes.includes(file.type)) {
            setErrors(prev => ({ ...prev, media: 'Please upload a valid image or video file' }));
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            setErrors(prev => ({ ...prev, media: 'File size must be less than 50MB' }));
            return;
        }

        setMediaFile(file);
        clearFieldError('media');
        const reader = new FileReader();
        reader.onloadend = () => setMediaPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveMedia = () => {
        setMediaFile(null);
        setMediaPreview(null);
    };

    const getProductImageUrl = (photo) => {
        if (!photo) return '';
        if (photo.startsWith('http')) return photo;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${photo}`;
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim())        newErrors.title    = 'Title is required';
        if (!formData.caption.trim())      newErrors.caption  = 'Caption is required';
        if (!mediaFile)                    newErrors.media    = 'Please upload an image or video';
        if (selectedProducts.length === 0) newErrors.products = 'Please tag at least one product';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setLoading(true);
            const submitData = new FormData();
            submitData.append('title',       formData.title.trim());
            submitData.append('caption',     formData.caption.trim());
            submitData.append('mediaFile',   mediaFile);
            submitData.append('contentType', mediaFile.type.startsWith('video') ? 'video' : 'image');
            submitData.append('tags',        JSON.stringify([]));
            submitData.append('products',    JSON.stringify(selectedProducts.map(p => p.ProductCode)));

            const response = await contentService.createPost(submitData);
            if (response.success) {
                toast.success('Post submitted for approval! You can view it in your profile.');
                navigate('/profile');
            } else {
                setErrors({ submit: response.message || 'Failed to create post' });
            }
        } catch (err) {
            console.error('Create post error:', err);
            setErrors({ submit: err.message || 'Failed to create post' });
            toast.error(err.message || 'Failed to create post');
        } finally {
            setLoading(false);
        }
    };

    const isVideo = mediaFile?.type.startsWith('video');

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }} className="glass-content-container">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <AddIcon sx={{ color: '#bc965e', fontSize: 32 }} />
                    <Typography variant="h4" className="glass-title">Create Post</Typography>
                </Box>
                <Typography variant="body1" className="glass-text-secondary">
                    Share your fashion moments with the community
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                Your post will be reviewed by our team before it appears on the feed. You must tag at least one product.
            </Alert>

            <Paper elevation={1} className="glass-content-container">
                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Title */}
                        <TextField
                            fullWidth
                            label="Title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Give your post a catchy title"
                            className="glass-input"
                            inputProps={{ maxLength: 100 }}
                            error={!!errors.title}
                            helperText={errors.title}
                        />

                        {/* Caption */}
                        <TextField
                            fullWidth
                            label="Caption"
                            name="caption"
                            value={formData.caption}
                            onChange={handleInputChange}
                            placeholder="Tell us about your post..."
                            multiline
                            rows={4}
                            className="glass-input"
                            inputProps={{ maxLength: 500 }}
                            error={!!errors.caption}
                            helperText={errors.caption}
                        />

                        {/* Media Upload */}
                        <Box>
                            <Typography variant="subtitle2" className="glass-text-primary" sx={{ mb: 1 }}>
                                Upload Photo or Video *
                            </Typography>

                            {!mediaPreview ? (
                                <Box
                                    sx={{
                                        border: `2px dashed ${errors.media ? '#d32f2f' : 'rgba(188,150,94,0.3)'}`,
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: errors.media ? '#d32f2f' : '#bc965e',
                                            backgroundColor: 'rgba(188,150,94,0.05)'
                                        }
                                    }}
                                    onClick={() => document.getElementById('media-upload').click()}
                                >
                                    <input
                                        id="media-upload"
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={handleMediaChange}
                                        style={{ display: 'none' }}
                                    />
                                    <PhotoCameraIcon
                                        sx={{ fontSize: 48, color: errors.media ? '#d32f2f' : '#bc965e', mb: 2 }}
                                    />
                                    <Typography variant="body1" className="glass-text-primary" gutterBottom>
                                        Click to upload photo or video
                                    </Typography>
                                    <Typography variant="caption" className="glass-text-secondary">
                                        Supports: JPG, PNG, GIF, WebP, MP4, WebM (Max 50MB)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
                                    {isVideo ? (
                                        <video src={mediaPreview} controls
                                            style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', backgroundColor: '#000' }} />
                                    ) : (
                                        <img src={mediaPreview} alt="Preview"
                                            style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', backgroundColor: '#000' }} />
                                    )}
                                    <IconButton onClick={handleRemoveMedia}
                                        sx={{
                                            position: 'absolute', top: 8, right: 8,
                                            backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                        }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            )}

                            {/* ── inline media error ── */}
                            {errors.media && (
                                <FormHelperText error sx={{ mt: 0.5, ml: 0.5 }}>
                                    {errors.media}
                                </FormHelperText>
                            )}
                        </Box>

                        {/* Product Tagging */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TagIcon sx={{ color: errors.products ? '#d32f2f' : '#bc965e', fontSize: 20 }} />
                                <Typography variant="subtitle2" className="glass-text-primary">
                                    Tag Products *
                                </Typography>
                            </Box>
                            <Typography variant="caption" className="glass-text-secondary" sx={{ display: 'block', mb: 1 }}>
                                Tag products from our collection that you're wearing or featuring
                            </Typography>

                            <Autocomplete
                                multiple
                                options={products}
                                getOptionLabel={(option) => `${option.ProductName} - ${option.ProductCode} - ${option.Color || 'N/A'}`}
                                value={selectedProducts}
                                onChange={(event, newValue) => {
                                    setSelectedProducts(newValue);
                                    if (newValue.length > 0) clearFieldError('products');
                                }}
                                loading={loadingProducts}
                                filterOptions={(options, { inputValue }) => {
                                    const s = inputValue.toLowerCase();
                                    return options.filter(o =>
                                        o.ProductName.toLowerCase().includes(s) ||
                                        o.ProductCode.toLowerCase().includes(s) ||
                                        (o.Color && o.Color.toLowerCase().includes(s))
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search by name, code, or color..."
                                        className="glass-input"
                                        error={!!errors.products}
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingProducts ? <CircularProgress size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                                        <Avatar src={getProductImageUrl(option.Photo)} alt={option.ProductName}
                                            sx={{ width: 60, height: 60 }} variant="rounded" />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" className="glass-text-primary" fontWeight={500}>
                                                {option.ProductName}
                                            </Typography>
                                            <Typography variant="caption" className="glass-text-secondary" sx={{ display: 'block' }}>
                                                Code: {option.ProductCode}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                {option.Color && (
                                                    <Chip label={option.Color} size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'rgba(188,150,94,0.1)' }} />
                                                )}
                                                <Typography variant="caption" className="glass-text-primary" fontWeight={600}>
                                                    ₹{option.Price}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.ProductCode}
                                            avatar={<Avatar src={getProductImageUrl(option.Photo)} />}
                                            label={`${option.ProductName} (${option.ProductCode})`}
                                            sx={{
                                                backgroundColor: 'rgba(188,150,94,0.1)',
                                                '& .MuiChip-deleteIcon': { color: '#bc965e' }
                                            }}
                                        />
                                    ))
                                }
                            />

                            {/* ── inline products error ── */}
                            {errors.products && (
                                <FormHelperText error sx={{ mt: 0.5, ml: 0.5 }}>
                                    {errors.products}
                                </FormHelperText>
                            )}
                        </Box>

                        {/* General submit error */}
                        {errors.submit && (
                            <Alert severity="error" onClose={() => clearFieldError('submit')}>
                                {errors.submit}
                            </Alert>
                        )}

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant="outlined" onClick={() => navigate('/profile')}
                                disabled={loading} className="glass-button-outlined">
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                                className="glass-button">
                                {loading ? 'Posting...' : 'Post'}
                            </Button>
                        </Box>

                    </Box>
                </form>
            </Paper>
        </Container>
    );
};

export default CreatePostPage;


